import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../components/Logo";
import ProgressSteps from "../../../components/ProgressSteps";
import {
  getEtablissementDirectory,
  getTypes,
  searchEtablissements,
  type PublicEtablissementSummary,
  type TypeOption,
} from "../../../api/authEtablissement";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const SIGNUP_STORAGE_KEY = "maps.signup";
const PAGE_SIZE = 5;
/** Sentinel pour "Tous les types" : Radix Select interdit une valeur d'item vide. */
const ALL_TYPES = "__all__";

export default function EtablissementSignupSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [results, setResults] = useState<PublicEtablissementSummary[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getTypes().then((result) => {
      if (result.ok && result.data?.length) setTypes(result.data);
    });
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    let cancelled = false;
    setLoading(true);
    setError("");

    const timer = setTimeout(
      async () => {
        if (trimmed.length >= 2) {
          const result = await searchEtablissements(trimmed);
          if (cancelled) return;
          if (result.ok) {
            setResults(result.data);
            setPagination({ page: 1, totalPages: 1 });
          } else {
            setError(result.message);
          }
        } else {
          const result = await getEtablissementDirectory({
            page: pagination.page,
            limit: PAGE_SIZE,
            estRevendique: false,
            type: typeFilter || undefined,
          });
          if (cancelled) return;
          if (result.ok) {
            setResults(result.data.data);
            setPagination({ page: result.data.pagination.page, totalPages: result.data.pagination.totalPages });
          } else {
            setError(result.message);
          }
        }
        if (!cancelled) setLoading(false);
      },
      trimmed.length >= 2 ? 300 : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, typeFilter, pagination.page]);

  const isBrowsing = query.trim().length < 2;

  const selectEtablissement = (item: PublicEtablissementSummary) => {
    if (item.estRevendique) return;
    try {
      localStorage.setItem(
        SIGNUP_STORAGE_KEY,
        JSON.stringify({
          mode: "claim",
          etablissementId: item.id,
          etablissementNom: item.nom,
          etablissementSigle: item.sigle,
          etablissementVille: item.ville ?? item.commune,
          etablissementType: item.type,
        })
      );
    } catch {
      /* ignore */
    }
    navigate("/etablissement/inscription/identifiants");
  };

  const notInList = () => {
    try {
      localStorage.removeItem(SIGNUP_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    navigate("/etablissement/inscription/informations");
  };

  return (
    <div className="h-screen overflow-hidden grid grid-cols-1 md:grid-cols-2 relative">
      {/* Mobile background image */}
      <div className="absolute inset-0 md:hidden">
        <img src={'/maps-login-side.jpg'} alt="signup-search" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Left: form */}
      <div className="relative flex items-start justify-center px-4 md:px-10 py-10 h-full overflow-y-auto thin-scrollbar">
        <div className="w-full max-w-md">
          <div className="flex justify-center"><Logo size={56} /></div>
          <div className="mt-6 text-sm" style={{ color: "#00553E" }}>
            <Link to="/etablissement/connexion" className="inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Retour
            </Link>
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-[#0B1F16]">
            Créer le compte de l'établissement
          </h1>
          <div className="mt-1 text-gray-500 font-semibold">Retrouvez votre établissement</div>
          <p className="mt-2 text-sm text-gray-600">
            Cherchez votre établissement dans l'annuaire pour revendiquer sa fiche, ou parcourez la liste ci-dessous.
          </p>

          <div className="mt-6 space-y-3">
            <input
              placeholder="Nom de l'établissement…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full h-12 rounded-[10px] border px-3 bg-white"
              style={{ borderColor: "#E5E7EB" }}
            />
            {isBrowsing && (
              <Select
                value={typeFilter || ALL_TYPES}
                onValueChange={(value) => {
                  setTypeFilter(value === ALL_TYPES ? "" : value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger
                  className="w-full h-12 rounded-[10px] border px-3 bg-white"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TYPES}>Tous les types</SelectItem>
                  {types.map((t) => (
                    <SelectItem
                      key={t.type ?? t.id ?? t.code ?? t.libelle ?? t.label}
                      value={t.type ?? t.code ?? t.libelle ?? t.label ?? ""}
                    >
                      {t.libelle ?? t.label ?? t.code ?? t.id ?? ""}
                      {typeof t.count === "number" ? ` (${t.count})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
              {error}
            </div>
          )}

          <div className="mt-4 space-y-2">
            {loading && <div className="text-sm text-gray-500 py-2">Recherche…</div>}
            {!loading && results.length === 0 && (
              <div className="text-sm text-gray-500 py-2">Aucun établissement trouvé.</div>
            )}
            {!loading &&
              results
                .filter((item) => !item.estRevendique)
                .slice(0, PAGE_SIZE)
                .map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectEtablissement(item)}
                    className="w-full text-left rounded-[10px] border px-3 py-2 hover:bg-[#F3FAF7] cursor-pointer"
                    style={{ borderColor: "#E5E7EB" }}
                  >
                    <div className="font-semibold text-[#0B1F16]">
                      {item.nom}
                      {item.sigle ? ` (${item.sigle})` : ""}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.type} — {item.ville ?? item.commune ?? "Localisation inconnue"}
                    </div>
                  </button>
                ))}
          </div>

          {isBrowsing && pagination.totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                className="cursor-pointer disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                ← Précédent
              </button>
              <span className="text-gray-500">
                Page {pagination.page} / {pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                className="cursor-pointer disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                Suivant →
              </button>
            </div>
          )}

          <div className="mt-6">
            <button
              type="button"
              onClick={notInList}
              className="w-full h-12 rounded-[10px] font-semibold cursor-pointer border"
              style={{ borderColor: "#00553E", color: "#00553E" }}
            >
              Mon établissement n'est pas dans la liste
            </button>
          </div>

          <div className="mt-8">
            <ProgressSteps step={1} total={5} />
          </div>
        </div>
      </div>

      {/* Right: image (desktop) */}
      <div className="relative hidden md:block">
        <img src={'/maps-login-side.jpg'} alt="signup-search" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
